import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Target, RefreshCw, Loader2 } from "lucide-react";

const REFRESH_MS = 60_000;

const BUCKET_META = {
  extracted:        { label: "Extracted",           color: "bg-emerald-500", tone: "text-emerald-900 bg-emerald-50" },
  transparency_url: { label: "Transparency URL",    color: "bg-sky-500",     tone: "text-sky-900 bg-sky-50" },
  manual_url:       { label: "Manual URL",          color: "bg-indigo-500",  tone: "text-indigo-900 bg-indigo-50" },
  queued:           { label: "Queued for retry",    color: "bg-amber-500",   tone: "text-amber-900 bg-amber-50" },
  generic_url:      { label: "Generic URL",         color: "bg-stone-400",   tone: "text-stone-900 bg-stone-100" },
  no_url:           { label: "No URL yet",          color: "bg-rose-400",    tone: "text-rose-900 bg-rose-50" },
};

/**
 * TopUnisCoverageTile — admin dashboard.
 *
 * Answers: "Of the top-200 UK unis by world rank, where do we stand on
 * offer-rate ingestion?" Bucketed so we can watch the Pass 1a
 * transparency-tuned Serper query lift the `transparency_url` slice.
 */
export default function TopUnisCoverageTile() {
  const [ukData, setUkData] = useState(null);
  const [usData, setUsData] = useState(null);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    try {
      const [uk, us] = await Promise.all([
        api.get("/admin/coverage/top-unis?country=UK&top_n=200"),
        api.get("/admin/coverage/top-unis?country=US&top_n=200"),
      ]);
      setUkData(uk.data);
      setUsData(us.data);
      setErr(null);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load coverage");
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { const t = setInterval(load, REFRESH_MS); return () => clearInterval(t); }, [load]);

  if (err) {
    return (
      <div data-testid="top-unis-coverage-tile-error"
           className="border border-rose-200 bg-rose-50/50 rounded-xl p-4 text-sm text-rose-900">
        Top-unis coverage tile failed: {err}
      </div>
    );
  }
  if (!ukData || !usData) {
    return (
      <div data-testid="top-unis-coverage-tile-loading"
           className="border border-stone-200 rounded-xl p-4 text-sm text-stone-500">
        <Loader2 className="inline h-4 w-4 animate-spin mr-1" />
        Loading top-unis coverage…
      </div>
    );
  }

  const renderCountry = (data) => {
    const total = data.observed || 1;
    const orderedBuckets = [
      "extracted", "transparency_url", "manual_url",
      "queued", "generic_url", "no_url",
    ];
    // Collapse any `failed_*` sub-buckets into generic_url for display —
    // computed on a fresh object every render so we never mutate state.
    const displayBuckets = { ...data.buckets };
    let failedTotal = 0;
    for (const [k, v] of Object.entries(displayBuckets)) {
      if (k.startsWith("failed_")) {
        failedTotal += v;
        delete displayBuckets[k];
      }
    }
    if (failedTotal) {
      displayBuckets.generic_url = (displayBuckets.generic_url || 0) + failedTotal;
    }

    return (
      <div data-testid={`top-unis-${data.country.toLowerCase()}`}
           className="rounded-lg border border-stone-100 p-4">
        <div className="flex items-baseline justify-between mb-3">
          <div className="font-semibold text-sm text-stone-900">
            {data.country} · top {data.top_n}
          </div>
          <div className="text-[11px] text-stone-500">
            {data.observed} unis observed
          </div>
        </div>

        <div className="flex h-3 rounded-full overflow-hidden bg-stone-100 mb-3">
          {orderedBuckets.map((k) => {
            const n = displayBuckets[k] || 0;
            if (!n) return null;
            return (
              <div key={k}
                   data-testid={`top-unis-${data.country.toLowerCase()}-${k}`}
                   className={`${BUCKET_META[k]?.color || 'bg-stone-300'}`}
                   style={{ width: `${(n / total) * 100}%` }}
                   title={`${BUCKET_META[k]?.label || k}: ${n} (${((n/total)*100).toFixed(1)}%)`}
              />
            );
          })}
        </div>

        <ul className="space-y-1">
          {orderedBuckets.map((k) => {
            const n = displayBuckets[k] || 0;
            if (!n) return null;
            const meta = BUCKET_META[k] || { label: k, tone: "text-stone-700 bg-stone-100" };
            return (
              <li key={k} className="text-xs flex items-center gap-2">
                <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium ${meta.tone}`}>
                  {meta.label}
                </span>
                <span className="font-mono text-stone-700">
                  {n} · {((n / total) * 100).toFixed(1)}%
                </span>
                {(data.samples[k] || []).slice(0, 1).map((s) => (
                  <span key={s.id} className="text-stone-400 truncate flex-1">
                    e.g. #{s.world_rank} {s.name}
                  </span>
                ))}
              </li>
            );
          })}
        </ul>
      </div>
    );
  };

  return (
    <div data-testid="top-unis-coverage-tile"
         className="border border-stone-200 rounded-xl p-5 bg-white shadow-sm">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex items-center gap-2">
          <div className="p-2 rounded-lg bg-sky-50">
            <Target className="h-5 w-5 text-sky-700" />
          </div>
          <div>
            <div className="text-sm font-semibold text-stone-900">
              Top-200 unis · admissions-URL coverage
            </div>
            <div className="text-xs text-stone-500">
              Extracted ≻ Transparency URL ≻ Manual ≻ Queued ≻ Generic ≻ None
            </div>
          </div>
        </div>
        <button
          data-testid="top-unis-refresh-btn"
          onClick={load}
          className="text-xs text-stone-500 hover:text-stone-900 flex items-center gap-1">
          <RefreshCw className="h-3.5 w-3.5" />
          Refresh
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {renderCountry(ukData)}
        {renderCountry(usData)}
      </div>

      <p className="mt-4 text-[11px] text-stone-500">
        The Serper Pass 1a query looks specifically for
        "transparency return" / "transparency information" pages so
        UK unis land in the sky-blue bar. Growth in that slice = more
        offer-rate wins per daemon tick.
      </p>
    </div>
  );
}
